class PmsMeasurementGuide extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = this.querySelector('[data-pms-measurement-form]');
    this.status = this.querySelector('[data-pms-measurement-status]');
    this.storageKey = this.dataset.storageKey || 'pms-customer-measurements-v1';
    if (!this.form) return;

    this.querySelector('[data-pms-save-device]')?.addEventListener('click', () => this.save());
    this.querySelector('[data-pms-copy]')?.addEventListener('click', () => this.copy());
    this.querySelector('[data-pms-print]')?.addEventListener('click', () => window.print());
    this.querySelector('[data-pms-clear]')?.addEventListener('click', () => this.clear());

    this.restore();
  }

  values() {
    const fields = new FormData(this.form);
    return Object.fromEntries(Array.from(fields.entries()).map(([key, value]) => [key, String(value).trim()]));
  }

  hasValues(values) {
    return Object.values(values).some(Boolean);
  }

  valid() {
    if (this.form.checkValidity()) return true;
    this.form.reportValidity();
    this.setStatus('Check the highlighted measurement before continuing.', 'error');
    return false;
  }

  setStatus(message, state = '') {
    if (!this.status) return;
    this.status.textContent = message;
    if (state) this.status.dataset.state = state;
    else delete this.status.dataset.state;
  }

  save() {
    const values = this.values();
    if (!this.hasValues(values)) {
      this.setStatus('Enter at least one measurement before saving.', 'error');
      return;
    }
    if (!this.valid()) return;

    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        version: 1,
        savedAt: new Date().toISOString(),
        values
      }));
      this.setStatus('Saved in this browser on this device. Anyone using this browser may be able to view it. It has not been added to a Shopify customer profile.', 'success');
    } catch (error) {
      this.setStatus('This browser would not allow local saving. Copy or print the worksheet instead.', 'error');
    }
  }

  restore() {
    let saved;
    try {
      saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
    } catch (error) {
      return;
    }
    if (!saved?.values) return;

    Object.entries(saved.values).forEach(([name, value]) => {
      const field = this.form.elements.namedItem(name);
      if (field && typeof value === 'string') field.value = value;
    });
    this.setStatus('Your measurement draft was restored from this browser.', 'success');
  }

  summary() {
    const values = this.values();
    const rows = [
      ['Neck', values.neck, 'in'],
      ['Shirt sleeve', values.shirt_sleeve, 'in'],
      ['Chest', values.chest, 'in'],
      ['Overarm', values.overarm, 'in'],
      ['Trouser waist', values.trouser_waist, 'in'],
      ['Seat / hip', values.seat, 'in'],
      ['Inseam', values.inseam, 'in'],
      ['Height', values.height, ''],
      ['Coat length preference', values.coat_length, ''],
      ['Fit notes', values.fit_notes, '']
    ];

    return [
      "Pomeroy's Measurement Worksheet",
      `Prepared: ${new Date().toLocaleDateString()}`,
      '',
      ...rows.filter(([, value]) => value).map(([label, value, unit]) => `${label}: ${value}${unit ? ` ${unit}` : ''}`),
      '',
      'Self-reported measurements should be verified before alteration.'
    ].join('\n');
  }

  async copy() {
    const values = this.values();
    if (!this.hasValues(values)) {
      this.setStatus('Enter at least one measurement before copying.', 'error');
      return;
    }
    if (!this.valid()) return;

    const text = this.summary();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const helper = document.createElement('textarea');
        helper.value = text;
        helper.setAttribute('readonly', '');
        helper.style.position = 'fixed';
        helper.style.opacity = '0';
        document.body.appendChild(helper);
        helper.select();
        if (!document.execCommand('copy')) throw new Error('Copy failed');
        helper.remove();
      }
      this.setStatus('Measurements copied. You can paste them into an email, note, or appointment request.', 'success');
    } catch (error) {
      this.setStatus('Automatic copying was blocked. Print the worksheet or copy each field manually.', 'error');
    }
  }

  clear() {
    if (!this.hasValues(this.values())) {
      this.setStatus('The worksheet is already empty.');
      return;
    }
    if (!window.confirm('Clear every measurement from this worksheet and remove the saved draft on this device?')) return;

    this.form.reset();
    try { localStorage.removeItem(this.storageKey); } catch (error) { /* No action needed. */ }
    this.setStatus('Worksheet and device draft cleared.', 'success');
  }
}

if (!customElements.get('pms-measurement-guide')) {
  customElements.define('pms-measurement-guide', PmsMeasurementGuide);
}
